import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Pencil } from "lucide-react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { formatCurrency } from "@/components/deals/dealFormat";
import { extractErrorMessage } from "@/api/client";
import { dealsApi } from "@/api/deals";
import {
  dealDetailKeys,
  useConfirmBrokerFeeAuthorization,
  useUpdateBrokerFeeAuthorization,
} from "@/hooks/deals/useDealDetail";
import {
  DEAL_PERMISSIONS,
  type BrokerCashAtClosingMode,
  type BrokerFeeAuthorizationDto,
  type BrokerFeeMonthlyMode,
} from "@/types/deals";

interface Props {
  deal: { id: string };
}

const MODE_LABEL: Record<BrokerFeeMonthlyMode, string> = {
  none: "No monthly fee",
  percent_of_seller_share: "% of seller's monthly share",
  flat_monthly: "Flat monthly amount",
};

const CASH_MODE_LABEL: Record<BrokerCashAtClosingMode, string> = {
  none: "No cash at closing",
  flat: "Flat amount",
  percent_of_down_payment: "% of down payment",
};

/**
 * The one line that keeps this figure apart from the deal-structure "cash at
 * closing" on the Overview tab — the buyer's §3 contribution, an unrelated
 * concept with a near-identical name. Rendered on EVERY mode, including
 * "none", because the disambiguation is what the line is for; it is not a
 * footnote about a value that happens to be present.
 */
const SELLER_TO_BROKER_NOTE = "Paid directly seller → broker (not Phoenix-administered).";

/** Render a stored FRACTION (0.05) as a percent string ("5%"). Trims trailing
 *  zeros so 0.025 → "2.5%" and 0.05 → "5%". */
function formatPercent(fraction: number): string {
  const pct = fraction * 100;
  return `${Number(pct.toFixed(4))}%`;
}

/** The monthly fee component — the only money-driving one. */
function monthlyFeeDisplay(dto: BrokerFeeAuthorizationDto): string {
  switch (dto.monthlyFeeMode) {
    case "percent_of_seller_share":
      return `${formatPercent(dto.monthlyFeeAmount)} of seller's monthly share`;
    case "flat_monthly":
      return `${formatCurrency(dto.monthlyFeeAmount)}/month`;
    case "none":
    default:
      return "No monthly fee";
  }
}

/**
 * The broker's cash at closing, in the mode it was authored in. The dollar
 * figure is always the server's `resolvedCashAtClosingAmount` — never
 * recomputed here, so the card and the record cannot disagree. A percentage
 * with nothing to resolve against reads as unresolved rather than as $0.
 */
function cashAtClosingDisplay(dto: BrokerFeeAuthorizationDto): {
  primary: string;
  unresolvedNote: string | null;
} {
  switch (dto.cashAtClosingMode) {
    case "flat":
      return { primary: formatCurrency(dto.resolvedCashAtClosingAmount), unresolvedNote: null };
    case "percent_of_down_payment": {
      const pct =
        dto.cashAtClosingPercent != null ? formatPercent(dto.cashAtClosingPercent) : "—";
      const base = `${pct} of down payment`;
      return dto.resolvedCashAtClosingAmount != null
        ? {
            primary: `${base} — ${formatCurrency(dto.resolvedCashAtClosingAmount)}`,
            unresolvedNote: null,
          }
        : { primary: base, unresolvedNote: "Down payment not on file — amount unresolved" };
    }
    case "none":
    default:
      return { primary: "No cash at closing", unresolvedNote: null };
  }
}

/**
 * Broker Fee Authorization — surfaces the broker's compensation terms on the
 * deal Intake tab: the recurring monthly fee (the money-driving component), the
 * broker's one-time cash at closing (a flat amount or a percentage of the
 * buyer's down payment, paid seller → broker off Phoenix's rails), and the
 * dormant investor-phase sale fee, plus the verification state. Loan
 * administrators (Deals.Update) can edit the terms and confirm them; editing
 * resets verification server-side, so the card returns to "Not yet verified"
 * after an edit. Self-gated on Deals.Read — renders nothing when the user can't
 * read.
 */
export function BrokerFeeAuthorizationCard({ deal }: Props) {
  const { hasPermission } = useAuth();
  const fmt = useDateFormatter();
  const canRead = hasPermission(DEAL_PERMISSIONS.Read);
  const canEdit = hasPermission(DEAL_PERMISSIONS.Update);
  const [editOpen, setEditOpen] = useState(false);

  const confirmMutation = useConfirmBrokerFeeAuthorization(deal.id);
  const adoptMutation = useUpdateBrokerFeeAuthorization(deal.id);
  const toast = useToast();

  const query = useQuery({
    queryKey: dealDetailKeys.brokerFeeAuthorization(deal.id),
    queryFn: () => dealsApi.brokerFeeAuthorization(deal.id),
    enabled: canRead,
  });

  // Section is visible only to users who can read the deal.
  if (!canRead) return null;

  const dto = query.data;
  const cash = dto ? cashAtClosingDisplay(dto) : null;

  // The deal's own down payment has moved away from the basis this fee was
  // sized against. Only meaningful in percent mode — in the other modes the
  // basis is neither shown nor used, so offering to adopt a figure would be
  // noise about a number the staff member cannot see.
  const showDriftNotice =
    !!dto &&
    dto.cashAtClosingMode === "percent_of_down_payment" &&
    dto.dealDownPaymentAmount != null &&
    dto.dealDownPaymentAmount !== dto.downPaymentBasisAmount;

  function adoptDealDownPayment() {
    if (!dto) return;
    adoptMutation.mutate(
      {
        monthlyFeeMode: dto.monthlyFeeMode,
        monthlyFeeAmount: dto.monthlyFeeAmount,
        cashAtClosingAmount: dto.cashAtClosingAmount,
        saleFeePercent: dto.saleFeePercent,
        cashAtClosingMode: dto.cashAtClosingMode,
        cashAtClosingPercent: dto.cashAtClosingPercent,
        downPaymentBasisAmount: dto.dealDownPaymentAmount,
      },
      {
        onSuccess: () => toast.success("Down payment basis updated."),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  }

  return (
    <section className="rounded-lg border border-border-light bg-white-pure p-space-4">
      <div className="flex items-center justify-between mb-space-3">
        <h2 className="font-semibold text-onyx m-0">Broker fee authorization</h2>
        {canEdit && dto && (
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => setEditOpen(true)}
            className="gap-space-1"
          >
            <Pencil size={14} aria-hidden />
            Edit terms
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : !dto || !cash ? (
        <p className="text-small text-gray-mid m-0">
          Broker fee authorization is not available for this deal.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-space-3 m-0">
            <Component label="Monthly fee">
              <span className="text-onyx">{monthlyFeeDisplay(dto)}</span>
            </Component>

            <Component label="Broker cash at closing">
              <span className="text-onyx">{cash.primary}</span>
              {cash.unresolvedNote && (
                <p className="text-caption text-gray-mid m-0 mt-space-1">{cash.unresolvedNote}</p>
              )}
              <p className="text-caption text-gray-mid m-0 mt-space-1">{SELLER_TO_BROKER_NOTE}</p>
            </Component>

            {dto.saleFeePercent != null && (
              <Component label="Sale fee">
                <span className="text-onyx">{formatPercent(dto.saleFeePercent)}</span>
                <p className="text-caption text-gray-mid m-0 mt-space-1">
                  Investor-phase — not yet active.
                </p>
              </Component>
            )}
          </dl>

          {canEdit && showDriftNotice && (
            <div className="mt-space-3 rounded-md border border-border-light bg-white-warm p-space-3 flex items-start justify-between gap-space-3 flex-wrap">
              <p className="text-small text-onyx m-0">
                {dto.downPaymentBasisAmount == null
                  ? `The deal's down payment is ${formatCurrency(dto.dealDownPaymentAmount)}, and this fee has no basis recorded yet.`
                  : `The deal's down payment is ${formatCurrency(dto.dealDownPaymentAmount)}, but this fee is based on ${formatCurrency(dto.downPaymentBasisAmount)}.`}{" "}
                <span className="text-gray-mid">
                  Adopting the deal's figure resets verification.
                </span>
              </p>
              <Button
                size="sm"
                variant="tertiary"
                onClick={adoptDealDownPayment}
                loading={adoptMutation.isPending}
              >
                Use the deal's figure
              </Button>
            </div>
          )}

          <div className="mt-space-4 pt-space-3 border-t border-border-light flex items-center justify-between gap-space-3 flex-wrap">
            {dto.isVerified ? (
              <div className="flex items-center gap-space-2">
                <Badge status="finalized" className="gap-space-1">
                  <BadgeCheck size={12} aria-hidden />
                  Verified
                </Badge>
                <span className="text-small text-gray-mid">
                  by {dto.verifiedByName ?? "—"}
                  {dto.verifiedAt ? ` on ${fmt.date(dto.verifiedAt)}` : ""}
                </span>
              </div>
            ) : (
              <span className="text-small text-gray-mid italic">Not yet verified</span>
            )}

            {canEdit && !dto.isVerified && (
              <div className="flex items-center gap-space-3 flex-wrap justify-end">
                {!dto.canVerify && dto.verifyBlockedReason && (
                  <span className="text-small text-gray-mid">{dto.verifyBlockedReason}</span>
                )}
                <Button
                  size="sm"
                  disabled={!dto.canVerify}
                  onClick={() =>
                    confirmMutation.mutate(undefined, {
                      onSuccess: () => toast.success("Broker fee terms confirmed."),
                      onError: (err) => toast.error(extractErrorMessage(err)),
                    })
                  }
                  loading={confirmMutation.isPending}
                >
                  Confirm terms
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {dto && (
        <BrokerFeeAuthorizationEditModal
          open={editOpen}
          dealId={deal.id}
          dto={dto}
          onClose={() => setEditOpen(false)}
        />
      )}
    </section>
  );
}

function Component({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small m-0 mt-[2px]">{children}</dd>
    </div>
  );
}

interface ModalProps {
  open: boolean;
  dealId: string;
  dto: BrokerFeeAuthorizationDto;
  onClose: () => void;
}

/**
 * Edit form for the broker's compensation terms. The UI works in PERCENT but
 * the API stores FRACTIONS, so the percent-mode monthly amount, the
 * percent-mode cash at closing and the sale fee are ×100 on load and ÷100 on
 * save. Saving resets verification server-side.
 *
 * Each mode's figure gets its OWN state string, seeded once per open and never
 * re-seeded mid-session, so flipping the selector to look at the other option
 * does not destroy a number the staff member already typed.
 */
function BrokerFeeAuthorizationEditModal({ open, dealId, dto, onClose }: ModalProps) {
  const toast = useToast();
  const mutation = useUpdateBrokerFeeAuthorization(dealId);

  const [mode, setMode] = useState<BrokerFeeMonthlyMode>(dto.monthlyFeeMode);
  // In percent mode the field holds a PERCENT (5), in flat mode a $ amount.
  const [monthlyAmount, setMonthlyAmount] = useState("");
  const [cashMode, setCashMode] = useState<BrokerCashAtClosingMode>(dto.cashAtClosingMode);
  // Two independent strings — see the header note.
  const [cashFlatAmount, setCashFlatAmount] = useState("");
  const [cashPercent, setCashPercent] = useState("");
  const [downPaymentBasis, setDownPaymentBasis] = useState("");
  const [saleFeePercent, setSaleFeePercent] = useState("");

  // Re-seed the form from the current terms whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setMode(dto.monthlyFeeMode);
    setMonthlyAmount(
      dto.monthlyFeeMode === "none"
        ? ""
        : dto.monthlyFeeMode === "percent_of_seller_share"
          ? String(Number((dto.monthlyFeeAmount * 100).toFixed(4)))
          : String(dto.monthlyFeeAmount),
    );
    setCashMode(dto.cashAtClosingMode);
    setCashFlatAmount(dto.cashAtClosingAmount != null ? String(dto.cashAtClosingAmount) : "");
    setCashPercent(
      dto.cashAtClosingPercent != null
        ? String(Number((dto.cashAtClosingPercent * 100).toFixed(4)))
        : "",
    );
    // No basis of its own yet → start from the deal's own down payment, which
    // is what staff would otherwise copy across by hand. Still overridable.
    setDownPaymentBasis(
      dto.downPaymentBasisAmount != null
        ? String(dto.downPaymentBasisAmount)
        : dto.dealDownPaymentAmount != null
          ? String(dto.dealDownPaymentAmount)
          : "",
    );
    setSaleFeePercent(
      dto.saleFeePercent != null ? String(Number((dto.saleFeePercent * 100).toFixed(4))) : "",
    );
  }, [open, dto]);

  function handleSave() {
    const amountNum = mode === "none" ? 0 : Number(monthlyAmount);
    const monthlyFeeAmount =
      mode === "percent_of_seller_share" ? amountNum / 100 : mode === "flat_monthly" ? amountNum : 0;
    const cashFlatTrim = cashFlatAmount.trim();
    const cashPctTrim = cashPercent.trim();
    const basisTrim = downPaymentBasis.trim();
    const saleTrim = saleFeePercent.trim();

    mutation.mutate(
      {
        monthlyFeeMode: mode,
        monthlyFeeAmount,
        cashAtClosingMode: cashMode,
        // Exactly one figure per mode; the server coerces the others to null
        // too, so a stale value can never be read back.
        cashAtClosingAmount: cashMode === "flat" && cashFlatTrim !== "" ? Number(cashFlatTrim) : null,
        cashAtClosingPercent:
          cashMode === "percent_of_down_payment" && cashPctTrim !== ""
            ? Number((Number(cashPctTrim) / 100).toFixed(4))
            : null,
        downPaymentBasisAmount: basisTrim === "" ? null : Number(basisTrim),
        saleFeePercent: saleTrim === "" ? null : Number(saleTrim) / 100,
      },
      {
        onSuccess: () => {
          toast.success("Broker fee terms updated.");
          onClose();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  }

  // Percent-mode amount is a fraction < 1, so reject ≥ 100%; flat must be ≥ 0.
  const amountNum = Number(monthlyAmount);
  const monthlyValid =
    mode === "none" ||
    (monthlyAmount.trim() !== "" &&
      Number.isFinite(amountNum) &&
      amountNum >= 0 &&
      (mode !== "percent_of_seller_share" || amountNum < 100));
  const saleNum = Number(saleFeePercent);
  const saleValid =
    saleFeePercent.trim() === "" || (Number.isFinite(saleNum) && saleNum >= 0 && saleNum < 100);
  const cashFlatNum = Number(cashFlatAmount);
  const cashPercentNum = Number(cashPercent);
  const basisNum = Number(downPaymentBasis);
  const cashValid =
    cashMode === "none"
      ? true
      : cashMode === "flat"
        ? cashFlatAmount.trim() !== "" && Number.isFinite(cashFlatNum) && cashFlatNum >= 0
        : cashPercent.trim() !== "" &&
          Number.isFinite(cashPercentNum) &&
          cashPercentNum >= 0 &&
          cashPercentNum < 100;
  // The basis may be left blank — verification is what waits on it, not the save.
  const basisValid =
    downPaymentBasis.trim() === "" || (Number.isFinite(basisNum) && basisNum >= 0);
  const valid = monthlyValid && saleValid && cashValid && basisValid;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Edit broker fee terms"
      description={
        dto.brokerName
          ? `${dto.brokerName} — confirming these terms is a separate step.`
          : "Confirming these terms is a separate step."
      }
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!valid} loading={mutation.isPending}>
            Save terms
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <p className="text-small text-gray-mid m-0">
          Saving updates the terms and resets verification — confirm the terms again afterwards.
        </p>

        <FormField label="Monthly fee" htmlFor="bfa-mode">
          <Select
            id="bfa-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as BrokerFeeMonthlyMode)}
          >
            {(Object.keys(MODE_LABEL) as BrokerFeeMonthlyMode[]).map((m) => (
              <option key={m} value={m}>
                {MODE_LABEL[m]}
              </option>
            ))}
          </Select>
        </FormField>

        {mode === "percent_of_seller_share" && (
          <FormField
            label="Percent of seller's monthly share"
            htmlFor="bfa-amount-pct"
            helperText="Entered as a percent (e.g. 5 for 5%); must be under 100%."
          >
            <Input
              id="bfa-amount-pct"
              type="number"
              min="0"
              max="100"
              step="0.01"
              inputMode="decimal"
              value={monthlyAmount}
              onChange={(e) => setMonthlyAmount(e.target.value)}
              placeholder="5"
            />
          </FormField>
        )}

        {mode === "flat_monthly" && (
          <FormField label="Flat monthly amount (USD)" htmlFor="bfa-amount-flat">
            <Input
              id="bfa-amount-flat"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={monthlyAmount}
              onChange={(e) => setMonthlyAmount(e.target.value)}
              placeholder="0.00"
            />
          </FormField>
        )}

        <FormField
          label="Broker cash at closing"
          htmlFor="bfa-cash-mode"
          helperText={SELLER_TO_BROKER_NOTE}
        >
          <Select
            id="bfa-cash-mode"
            value={cashMode}
            onChange={(e) => setCashMode(e.target.value as BrokerCashAtClosingMode)}
          >
            {(Object.keys(CASH_MODE_LABEL) as BrokerCashAtClosingMode[]).map((m) => (
              <option key={m} value={m}>
                {CASH_MODE_LABEL[m]}
              </option>
            ))}
          </Select>
        </FormField>

        {cashMode === "flat" && (
          <FormField label="Flat amount (USD)" htmlFor="bfa-cash-flat">
            <Input
              id="bfa-cash-flat"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={cashFlatAmount}
              onChange={(e) => setCashFlatAmount(e.target.value)}
              placeholder="0.00"
            />
          </FormField>
        )}

        {cashMode === "percent_of_down_payment" && (
          <>
            <FormField
              label="Percent of down payment"
              htmlFor="bfa-cash-pct"
              helperText="Entered as a percent (e.g. 10 for 10%); must be under 100%."
            >
              <Input
                id="bfa-cash-pct"
                type="number"
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
                value={cashPercent}
                onChange={(e) => setCashPercent(e.target.value)}
                placeholder="10"
              />
            </FormField>

            <FormField
              label="Down payment (basis for the percentage)"
              htmlFor="bfa-cash-basis"
              helperText="Leave blank if it isn't known yet — the terms can be saved, but not verified, without it."
            >
              <Input
                id="bfa-cash-basis"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={downPaymentBasis}
                onChange={(e) => setDownPaymentBasis(e.target.value)}
                placeholder="0.00"
              />
            </FormField>
          </>
        )}

        <FormField
          label="Sale fee (%)"
          htmlFor="bfa-sale"
          helperText="Optional — investor-phase, not yet active. Entered as a percent; must be under 100%."
        >
          <Input
            id="bfa-sale"
            type="number"
            min="0"
            max="100"
            step="0.01"
            inputMode="decimal"
            value={saleFeePercent}
            onChange={(e) => setSaleFeePercent(e.target.value)}
            placeholder="2"
          />
        </FormField>
      </div>
    </Modal>
  );
}
